import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { useDebouncedValue } from './useDebouncedValue';
import type { BestellungenResponse, DashboardSearch } from '@/types/dashboard';
import { melde } from '@/lib/nutzung';

function buildQueryString(s: DashboardSearch): string {
  const p = new URLSearchParams();
  if (s.q && s.q.trim() !== '') p.set('q', s.q.trim());
  // 'alle' ist der Default — nicht in die URL schreiben, sonst steht
  // dort dauerhaft ein Parameter ohne Wirkung.
  if (s.quelle && s.quelle !== 'alle') p.set('quelle', s.quelle);
  if (s.zustaende && s.zustaende !== '') p.set('zustaende', s.zustaende);
  if (s.erfasser && s.erfasser !== '') p.set('erfasser', s.erfasser);
  if (s.mein) p.set('mein', '1');
  if (s.alter_min !== undefined) p.set('alter_min', String(s.alter_min));
  if (s.alter_max !== undefined) p.set('alter_max', String(s.alter_max));
  if (s.verlag_id !== undefined) p.set('verlag_id', String(s.verlag_id));
  if (s.versandart_id !== undefined) p.set('versandart_id', String(s.versandart_id));
  if (s.nur_ueberfaellig) p.set('nur_ueberfaellig', '1');
  if (s.beschaffung_offen) p.set('beschaffung_offen', '1');
  if (s.nachlieferung) p.set('nachlieferung', '1');
  // Archiv-Modus (A10.4). 'aus' ist der Default und kommt nicht in die
  // Adresse — sonst stuende dort dauerhaft ein Parameter ohne Wirkung.
  if (s.archiv && s.archiv !== 'aus') p.set('archiv', s.archiv);
  if (s.sort) p.set('sort', s.sort);
  return p.toString();
}

/**
 * Die Treffer im Archiv zu einem Suchbegriff (E12, A10.5).
 *
 * WARUM EINE ZWEITE ABFRAGE UND NICHT EIN ZUSATZBLOCK IN DER ERSTEN
 *   Die Gruppe ist eine eigene Liste mit eigener Ueberschrift und eigener
 *   Zahl. Sie aus derselben Antwort zu schneiden hiesse, im Endpunkt
 *   zwei Ergebnismengen zu fuehren, die verschieden gefiltert sind — und
 *   dieselbe Sortierung, Zaehlung und Begrenzung ein zweites Mal
 *   aufzuschreiben. Als eigene Abfrage ist es dieselbe Mechanik wie
 *   ueberall sonst, nur mit `archiv=nur`.
 *
 * Ohne Suchbegriff laeuft sie gar nicht: das Dashboard zeigt Aktive,
 * Punkt. Die Gruppe erscheint nur, wenn jemand wirklich sucht.
 */
export function useArchivTreffer(search: DashboardSearch) {
  const debouncedQ = useDebouncedValue(search.q ?? '', 300);
  const sucht = debouncedQ.trim() !== '';
  const qs = buildQueryString({ q: debouncedQ, archiv: 'nur', sort: search.sort });
  return useQuery({
    queryKey: ['bestellungen', 'archiv', qs],
    queryFn: () => apiFetch<BestellungenResponse>(`/bestellungen.php?${qs}`),
    enabled: sucht,
    placeholderData: keepPreviousData,
    staleTime: 30_000
  });
}

export function useBestellungen(search: DashboardSearch) {
  const debouncedQ = useDebouncedValue(search.q ?? '', 300);
  const effective: DashboardSearch = { ...search, q: debouncedQ };
  const qs = buildQueryString(effective);
  return useQuery({
    queryKey: ['bestellungen', qs],
    queryFn: async () => {
      // Nutzungsmessung: Ladezeit des Dashboards (wird es langsamer, wenn
      // es voller wird?).
      const start = performance.now();
      const res = await apiFetch<BestellungenResponse>(`/bestellungen.php${qs ? '?' + qs : ''}`);
      melde('ladezeit', { ort: 'dashboard', wert: performance.now() - start });
      return res;
    },
    // Beim Tippen aendert sich der Query-Key. Ohne keepPreviousData ginge die
    // Query in den Pending-Zustand und die Liste waere kurz leer — zusammen
    // mit dem frueheren Early-Return war das die Ursache des Fokusverlusts (B1).
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false
  });
}
